/**
 * Composite hook that wires all shared layer hooks for a Nowcasting map instance.
 *
 * When a new layer type is added to the Nowcasting page, add it here once and
 * it automatically applies to main, split, and PiP maps.
 */
import { useRef } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { TioTileField } from '../../api/tomorrowIoTilesService';
import type { EventWithCircuit } from '../../api/eventsService';
import type { CircuitWithGeoJSON } from '../../api/circuitsService';
import type { StationData } from '../../api/stationObservationsService';
import type { RadarTile } from './useDataFetching';
import { LRUImageCache } from './radarPreloader';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';
import { useCircuitLayer } from './useCircuitLayer';
import { useWindArrowsLayer } from './useWindArrowsLayer';
import {
  useCircleOverlayLayer,
  useRadarBboxOverlayLayer,
  useRadiusCirclesLayer,
  useLayerOrder,
} from './useMapOverlayLayers';
import { useRadarLayer } from './useRadarLayer';
import { useTioLayer } from './useTioLayer';
import { getMapIds, type MapRole } from './nowcastingMapIds';

export interface UseMapLayersParams {
  mapRef: React.RefObject<MapboxMap | null>;
  role: MapRole;
  enabled: boolean;
  styleVersion: number;

  // Event & circuit
  selectedEvent: EventWithCircuit | null;
  circuitData: CircuitWithGeoJSON | null;
  trackLineWidth?: number;

  // Wind arrows
  showWindArrows: boolean;
  stations: StationData[];
  tiles: RadarTile[];
  currentIndex: number;

  // Overlays
  isCircleOverlayVisible: boolean;
  showRadarBbox: boolean;
  showRadiusCircles: boolean;
  radiusCircleCount: number;
  radiusCircleDistance: number;

  // Radar
  extendedTiles: RadarTile[];
  radarOpacity: number;
  fps: number;
  tilesSource: string;
  mapTileProvider: MapTileProvider;
  numericBlobUrl?: string | null;

  // TIO
  tioTimestamps: string[];
  tioCurrentIndex: number;
  tioTileField: TioTileField;
  tioActiveLayerRef: React.RefObject<'layer1' | 'layer2'>;
  tioTilePreloadRef?: React.RefObject<Set<string>>;

  /** Extra layer IDs to insert above stations in the z-order (e.g. rain-path, storm). */
  extraOrderLayers?: string[];
}

/**
 * Wires circuit, wind arrows, overlays, radar, and TIO hooks for one map.
 */
export function useMapLayers({
  mapRef,
  role,
  enabled,
  styleVersion,
  selectedEvent,
  circuitData,
  trackLineWidth,
  showWindArrows,
  stations,
  tiles,
  currentIndex,
  isCircleOverlayVisible,
  showRadarBbox,
  showRadiusCircles,
  radiusCircleCount,
  radiusCircleDistance,
  extendedTiles,
  radarOpacity,
  fps,
  tilesSource,
  mapTileProvider,
  numericBlobUrl,
  tioTimestamps,
  tioCurrentIndex,
  tioTileField,
  tioActiveLayerRef,
  tioTilePreloadRef,
  extraOrderLayers,
}: UseMapLayersParams): void {
  const ids = getMapIds(role);
  const radarCacheRef = useRef<LRUImageCache>(new LRUImageCache(80));

  useCircuitLayer({
    mapRef,
    circuitData: enabled ? circuitData : null,
    selectedEvent: enabled ? selectedEvent : null,
    styleVersion,
    trackLineWidth,
    ids,
  });

  useWindArrowsLayer({
    mapRef,
    enabled,
    selectedEvent,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    styleVersion,
    ids,
  });

  useLayerOrder({
    mapRef,
    enabled,
    styleVersion,
    ids,
    extraLayers: extraOrderLayers,
  });

  useCircleOverlayLayer({
    mapRef,
    enabled,
    selectedEvent,
    isVisible: isCircleOverlayVisible,
    styleVersion,
    sourceId: ids.circleOverlaySource,
    layerId: ids.circleOverlayLayer,
  });

  useRadarBboxOverlayLayer({
    mapRef,
    enabled,
    selectedEvent,
    isVisible: showRadarBbox,
    styleVersion,
    sourceId: ids.radarBboxSource,
    layerId: ids.radarBboxLayer,
  });

  useRadiusCirclesLayer({
    mapRef,
    enabled,
    selectedEvent,
    isVisible: showRadiusCircles,
    circleCount: radiusCircleCount,
    circleDistance: radiusCircleDistance,
    styleVersion,
    circleSourcePrefix: ids.radiusCircleSourcePrefix,
    circleLayerPrefix: ids.radiusCircleLayerPrefix,
    labelSourcePrefix: ids.radiusLabelSourcePrefix,
    labelLayerPrefix: ids.radiusLabelLayerPrefix,
  });

  useRadarLayer({
    mapRef,
    selectedEvent,
    extendedTiles,
    currentIndex,
    radarOpacity,
    fps,
    styleVersion,
    tilesSource,
    mapTileProvider,
    radarSourceId: ids.radarSourceA,
    radarLayerId: ids.radarLayerA,
    radarSourceId2: ids.radarSourceB,
    radarLayerId2: ids.radarLayerB,
    imageCacheRef: radarCacheRef,
    enabled,
    isTioActive: isTioProvider(mapTileProvider),
    resolvedTileUrl: mapTileProvider === 'radar-l2-numeric' ? numericBlobUrl : undefined,
  });

  useTioLayer({
    mapRef,
    enabled,
    tioSourceId: ids.tioSourceA,
    tioLayerId: ids.tioLayerA,
    tioSourceId2: ids.tioSourceB,
    tioLayerId2: ids.tioLayerB,
    tioActiveLayerRef,
    mapTileProvider,
    tioTimestamps,
    tioCurrentIndex,
    tioTileField,
    selectedEvent,
    radarOpacity,
    styleVersion,
    tioTilePreloadRef,
  });
}
